"use client";

import { DescriptionIcons } from "./DescriptionIcons.tsx";
import { DescriptionLinks } from "./DescriptionLinks";
import { ProductType } from "@/types/types.ts";
import styles from "./ProductDescriptionSideDrawer.module.scss";
import { useRef, useState } from "react";
import classNames from "classnames";
import { Icon, Link } from "@/types/prodcatBaseTypes.ts";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { Drawer } from "@JohnLewisPartnership/jl-design-system/dist/components/drawer/Drawer";
import { ListItem } from "@/components/__shared__/ListItem/ListItem.tsx";
import { ChevronRightOutlined as ChevronRight } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronRightOutlined";
import { withSideDrawerAnalytics } from "@/components/ProductSideDrawers/withSideDrawerAnalytics.tsx";

export type Props = {
  productType: ProductType;
  displayCode: string;
  description: string;
  icons: Icon[];
  links: Link[];
  endorsement:
    | {
        title: string;
        description?: string | null | undefined;
      }
    | undefined;
  hideDescriptionLinks?: boolean;
  showBeautySocietyLink?: boolean;
};

export const ProductDescriptionSideDrawerUI = ({
  productType,
  displayCode,
  description,
  icons,
  links,
  endorsement,
  hideDescriptionLinks,
  showBeautySocietyLink,
}: Props) => {
  const descriptionRef = useRef<HTMLDivElement>(null);

  const descriptionContainerStyle = styles.descriptionContainer;
  const shouldFadeAndTruncate = false;
  const containerStyles = shouldFadeAndTruncate
    ? classNames(descriptionContainerStyle, styles.truncated)
    : classNames(descriptionContainerStyle);

  const [open, setOpen] = useState(false);

  if (productType === "RetiredStaticBundle") return null;

  return (
    <>
      <ListItem
        label="Product description"
        onClick={withSideDrawerAnalytics("Product Description", () =>
          setOpen(true),
        )}
        trailingIcon={<ChevronRight size="medium" />}
      />
      <Drawer
        isOpen={open}
        onClose={() => {
          setOpen(false);
        }}
        heading="Product description"
      >
        <div className={containerStyles} ref={descriptionRef}>
          <p data-testid="description:code">
            <strong>Product code: {displayCode}</strong>
          </p>

          <DescriptionIcons icons={icons} isInDrawer={true} />

          <div
            className={styles.descriptionContent}
            data-testid="description:content"
            dangerouslySetInnerHTML={{
              __html: description,
            }}
          />
          {endorsement && (
            <div
              id="description:endorsement"
              data-testid="description:endorsement"
              className={styles.endorsement}
            >
              <h3
                className={styles.endorsementTitle}
                data-testid="description:endorsement:title"
              >
                {endorsement.title}
              </h3>
              {endorsement.description && (
                <div
                  data-testid="description:endorsement:description"
                  id="description:endorsement:description"
                  dangerouslySetInnerHTML={{ __html: endorsement.description }}
                />
              )}
            </div>
          )}

          {!hideDescriptionLinks && <DescriptionLinks links={links} />}

          {showBeautySocietyLink && (
            <div
              className={styles.beautySocityLink}
              data-testid="beauty-society:link"
            >
              <UnderlinedButton
                tag="a"
                target="_self"
                href={"https://www.johnlewis.com/our-services/beauty-society"}
                labelText="Read more about beauty consultations"
              />
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};
